/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React, { Component } from "react";
import { debounce } from "utils/lodashUtils";
import * as stores from "stores";
import hammer from "hammerjs/hammer";
import PageByPageViewCss from "./PageByPageView.css";
import addEventListener from "dc-preview-utils/src/Utils/DomUtils";
import { getCurrentPageIndexUtilForFitOnePageMode } from "utils/PageByPageViewUtils";
import { getPagesViewTotalHeight, getTotalHeightTillPageBottom, getThumbnailsViewYPos } from "selectors/pagedViewState";
import { ScrubberContainer } from "containers/scrubber/ScrubberContainer";
import { getCurrRHPFeature } from "selectors/appSelectors";
import { rhpDropinIds, ScrollDirection } from "constants/enums";
import { PAGES_VIEW_MARGIN_LEFT, PAGES_VIEW_MARGIN_RIGHT } from "constants/constants";
import { getThumbnailContainerTotalHeight } from "thumbnails/ThumbnailService";
import { isValidPageIndex, getPageHeight, getPageWidth } from "selectors/pageSelectors";
import CustomIcon from "dc-preview-utils/src/components/CustomIcon";
import CrossIcon from "dc-icons/viewer2021/S_CrossLargeWhite_18_N.svg";
import { isTouchDevice } from "dc-preview-utils/src/Utils/DeviceUtils";
import { getTotalPages } from "selectors/documentSelectors";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { getThumbnailColumnCount } from "selectors/thumbnailSelectors";

class PageByPageView extends Component {
  constructor(props) {
    super(props);
    this.setCurrPageZoomLevelToFitPage();
    this.shouldSendWheelEvent = true;
    this.updatePageNumWhileScrolling = true;
    this.wheelEventCalled = false;
    this.updatePageNumberOnScrollStart = true;
    this.lastScrollTop = 0;
    this.hManager = null;
    this.swipeGesture = null;
    this.state = { showCrossIcon: false };
    this._handleWheelEvent = this._handleWheelEvent.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleMouseDown = this._handleMouseDown.bind(this);
    this._updateScrollTopAsPerCurrentPage = this._updateScrollTopAsPerCurrentPage.bind(this);
    this._resetUpdatePageNumVar = this._resetUpdatePageNumVar.bind(this);
    this._onScrollEndHandler = this._onScrollEndHandler.bind(this);
  }

  _focus() {
    if (this.elementHandle) this.elementHandle.focus();
  }

  _resetUpdatePageNumVar() {
    this.updatePageNumberOnScrollStart = false;
    this.updatePageNumOnScrollTimeout = setTimeout(() => {
      this.updatePageNumberOnScrollStart = true;
    }, 800);
  }

  _handleThumbnailScroll = () => {
    // To scroll thumbnail view along with pages view
    if (getCurrRHPFeature(stores) === rhpDropinIds.THUMBNAILS) {
      let { start, end } = this.props.thumbnailsInView;
      const thumbnailColumnCount = getThumbnailColumnCount(stores);
      // Since there can be multiple thumbnails in single row of thumbnail panel, we are using the last thumbnail in the row (row having start index) as new start index
      // and first thumbnail in the row (row having last index) as new last index. This is done to set the range correctly, beyond which scroll will get trigerred
      start += thumbnailColumnCount - 1;
      end -= end % thumbnailColumnCount;
      const newPageIndex = getCurrentPageIndex(stores);
      const oldYPos = getThumbnailsViewYPos(stores);
      const height = getThumbnailContainerTotalHeight(stores);
      if (newPageIndex < start + 1) {
        let newPos = oldYPos - (start - newPageIndex + 1) * height;
        if (newPos < 0) {
          newPos = 0;
        }
        stores.pagedViewState.scrollThumbnailsView(newPos, true);
      } else if (newPageIndex > end - 1) {
        const newPos = oldYPos + (newPageIndex - end + 1) * height;
        stores.pagedViewState.scrollThumbnailsView(newPos, true);
      }
    }
  };

  _changePageOnScrollStart = (parentViewHeight, currPageIndex) => {
    // order of events : wheel < scroll. The code inside this(^) "else if" block should only be executed when the user deliberately scrolls, so a "wheelEventCalled" check is necessary.
    // next two blocks (if and else if) are for smoother scrolling. If users want to move to the next or prev page solely via scrolling, they should be able to do it with a single stroke.
    if (this.elementHandle.scrollTop > this.lastScrollTop) {
      const nextPage = currPageIndex + 1;
      if (isValidPageIndex(stores, nextPage)) {
        const extraPixels = 2;
        const nextPageScrollTop = getTotalHeightTillPageBottom(stores, nextPage - 1) + extraPixels;
        this.elementHandle.scrollTop = nextPageScrollTop;
        stores.documentStore.setCurrPageIndexForFitOnePageMode(nextPage);
      }
    } else if (this.elementHandle.scrollTop < this.lastScrollTop) {
      const prevPage = currPageIndex - 1;
      if (isValidPageIndex(stores, prevPage)) {
        const prevPageScrollTop = getTotalHeightTillPageBottom(stores, prevPage - 1);
        // adding extraPixels to avoid page change because of contnuos scroll event (preventing these scroll event is not possible)
        // Adding a wheelEventCalled check to resolve issues when user scrolls via clicking on the scrollbar (Bug ID - ADCS-43265)
        const extraPixels = prevPage > 0 ? (this.wheelEventCalled ? parentViewHeight / 5 : parentViewHeight - 2) : 0;
        this.elementHandle.scrollTop = prevPageScrollTop + extraPixels;
        stores.documentStore.setCurrPageIndexForFitOnePageMode(prevPage);
      }
    }
    this._resetUpdatePageNumVar();
  };

  _handlePagesViewScrollInZoomedInState = () => {
    const { pageHeight, parentViewHeight, currPageIndex } = this.props;
    let reqPagePositionFromTop = 0;
    if (pageHeight > parentViewHeight) {
      // To ensure a seamless scrolling experience when pagesView is zoomed in, the scroll top should be updated in a way that eliminates any visible gaps between pages.
      const origTopPositionForPage =
        (this.elementHandle.scrollTop - getTotalHeightTillPageBottom(stores, currPageIndex - 1)) % pageHeight;
      if (origTopPositionForPage >= 0) {
        reqPagePositionFromTop = -1 * origTopPositionForPage * (1 - parentViewHeight / pageHeight);
      }
    }
    return reqPagePositionFromTop;
  };

  _handleScrollEvent(evt) {
    stores.documentStore.updateScrollDirection(this.elementHandle.scrollTop);
    if (this.props.isFitOneFullPageMode) {
      if (this.elementHandle && this.props.shouldShowScrubber) {
        stores.pageByPageStore.setPageByPageScrollTop(this.elementHandle.scrollTop);
      }
      const { parentViewHeight, pageHeight, currPageIndex } = this.props;
      if (this.updatePageNumWhileScrolling) {
        const scrollTop = this.elementHandle.scrollTop;
        const lastPageIndex = getTotalPages(stores) - 1;
        const newPageIndex = getCurrentPageIndexUtilForFitOnePageMode(stores, scrollTop);
        if (newPageIndex !== currPageIndex) {
          if (newPageIndex === 0) {
            // After the user has finished scrolling and reaches the first page, the scrollTop should always be set to 0
            this.elementHandle.scrollTop = 0;
          } else if (currPageIndex === lastPageIndex && newPageIndex === lastPageIndex - 1 && this.wheelEventCalled) {
            // When the user is scrolling from the last page to the second-to-last page, a slight jump in the scrollbar should be visible to enhance the user experience.
            const newPageScrollTop = getTotalHeightTillPageBottom(stores, newPageIndex - 1) + parentViewHeight / 5;
            this.elementHandle.scrollTop = Math.min(scrollTop, newPageScrollTop);
          }
          stores.documentStore.setCurrPageIndexForFitOnePageMode(newPageIndex);
          this.updatePageNumberOnScrollStart = false;
        } else if (this.updatePageNumberOnScrollStart && parentViewHeight >= pageHeight) {
          this._changePageOnScrollStart(parentViewHeight, currPageIndex);
        }
      }
      let reqPagePositionFromTop = this._handlePagesViewScrollInZoomedInState();
      this.props.setPagePositionFromTop(reqPagePositionFromTop);
      this._handleThumbnailScroll();
      this.lastScrollTop = this.elementHandle.scrollTop;
    }
    this.props.onScroll(evt);
    this.wheelEventCalled = false;
  }

  _onScrollEndHandler(evt) {
    clearTimeout(this.updatePageNumOnScrollTimeout);
    this.updatePageNumberOnScrollStart = true;
    this.props.onScrollEnd(evt);
  }

  setCurrPageZoomLevelToFitPage() {
    const fitPageZoomLevel = stores.zoom.getFitPageZoomLevel(this.props.currPageIndex);
    stores.zoom.setPageZoomLevel(fitPageZoomLevel);
  }

  _handleWheelEvent(evt) {
    if (!this.wheelEventCalled) {
      this.wheelEventCalled = true;
    }
    if (this.shouldSendWheelEvent && this.props.isFullScreenMode) {
      this.shouldSendWheelEvent = false;
      this.props.handleWheelEvent(evt);
      setTimeout(() => {
        this.shouldSendWheelEvent = true;
      }, 1200);
    }
  }

  _handleMouseDown(evt) {
    this._mouseDownPos = {
      x: evt.clientX,
      y: evt.clientY
    };
  }

  _handleClick(evt) {
    const x = evt.clientX,
      y = evt.clientY;
    if (this._mouseDownPos && Math.abs(x - this._mouseDownPos.x) <= 4 && Math.abs(y - this._mouseDownPos.y) <= 4) {
      this.props.handleClick(evt);
    }
  }

  _updateScrollTopAsPerCurrentPage() {
    let scrollTopAsPerCurrentPage = getTotalHeightTillPageBottom(stores, this.props.currPageIndex - 1);
    // On certain systems, when users navigate between pages using keyboard arrow keys, the browser's scroll event is triggered simultaneously.
    // Preventing this browser scroll is currently not feasible, as we receive the event after the actual scroll has already happened.
    // The browser automatically scrolls the PDF document by a small distance (less than 1 pixel, observed) leading to calculation inaccuracies. Adding these extra pixels resolves the problem.
    const extraPixels = this.props.currPageIndex === 0 ? 0 : 1;
    this.elementHandle.scrollTop = scrollTopAsPerCurrentPage + extraPixels;
    // If scroll event is triggered after this, then do not update current page immediately [To fix ADCS-34841]
    this.updatePageNumWhileScrolling = false;
    if (this.updatePageNumberTimeout) {
      clearTimeout(this.updatePageNumberTimeout);
    }
    this.updatePageNumberTimeout = setTimeout(() => {
      this.updatePageNumWhileScrolling = true;
    }, 300);
  }

  _handleMouseMove(event) {
    if (this.props.isFullScreenMode) {
      const threshold = 100;
      if (event.clientY <= threshold && event.clientX >= this.props.viewPortWidth - threshold) {
        !this.state.showCrossIcon && this.setState({ showCrossIcon: true });
      } else if (this.state.showCrossIcon) {
        this.setState({ showCrossIcon: false });
      }
    }
  }

  componentDidMount() {
    if (this.elementHandle) {
      this.props.onPagesInViewChange({
        start: this.props.currPageIndex,
        end: this.props.currPageIndex
      });
      if (this.props.setFocusMethod) {
        this.props.setFocusMethod(this._focus.bind(this));
      }

      // create hammer manager to manage PageByPageView element
      this.hManager = new hammer.Manager(this.elementHandle);

      // adding swipe event
      this.swipeGesture = new hammer.Swipe();
      this.hManager.add(this.swipeGesture);
      this.hManager.on("swipe", this.props.handleSwipe);
      this._removeContextMenuHandler = addEventListener(this.elementHandle, "contextmenu", this.props.handleClick);
      this.props.isFullScreenMode && this.props.addInListOfVisitedPages();
      this.elementHandle.scrollTop = this.props.currPageIndex * this.props.parentViewHeight;
      this._removeScrollHandler = addEventListener(this.elementHandle, "scroll", this._handleScrollEvent.bind(this));
      if (!isTouchDevice()) {
        this._removeMouseMoveHandler = addEventListener(
          this.elementHandle,
          "mousemove",
          this._handleMouseMove.bind(this)
        );
      }
      this._removeScrollDebouncedHandler = addEventListener(
        this.elementHandle,
        "scroll",
        debounce(this._onScrollEndHandler, 300)
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isFitOneFullPageMode) {
      if (prevProps.currPageIndex !== this.props.currPageIndex) {
        let pageIndexAsPerScrollTop = getCurrentPageIndexUtilForFitOnePageMode(stores, this.elementHandle.scrollTop);
        if (pageIndexAsPerScrollTop !== this.props.currPageIndex) {
          // for handling the keyboard navigation cases (arrow keys, spacebar etc)
          this._updateScrollTopAsPerCurrentPage();
        }
      }

      if (this.props.showScrubberMidDiv && prevProps.scrollTopAsPerScrubber !== this.props.scrollTopAsPerScrubber) {
        this.elementHandle.scrollTop = this.props.scrollTopAsPerScrubber;
      }
    }
    if (prevProps.currPageIndex !== this.props.currPageIndex) {
      this.props.isFullScreenMode && this.setCurrPageZoomLevelToFitPage();
      this.props.onPagesInViewChange({
        start: this.props.currPageIndex,
        end: this.props.currPageIndex
      });
      // As full-screen mode lacks a scrollbar, we don't receive scroll events in that scenario. Therefore, we update the scroll direction based on page changes.
      if (this.props.isFullScreenMode) {
        if (prevProps.currPageIndex < this.props.currPageIndex) {
          stores.documentStore.scrollDirection = ScrollDirection.FORWARD;
        } else if (prevProps.currPageIndex > this.props.currPageIndex) {
          stores.documentStore.scrollDirection = ScrollDirection.BACKWARD;
        }
      }
    } else if (this.props.isFitOneFullPageMode && prevProps.zoomlevel !== this.props.zoomlevel) {
      // to keep page stable when zoom is in progress
      this._updateScrollTopAsPerCurrentPage();
    }
    this.props.isFullScreenMode && this.props.addInListOfVisitedPages();
  }

  componentWillUnmount() {
    if (this.hManager) {
      this.hManager.off("swipe", this.props.handleSwipe);
      this.hManager.remove(this.swipeGesture);
      this.swipeGesture = null;

      this.hManager.destroy();
      this.hManager = null;
    }
    this._removeContextMenuHandler && this._removeContextMenuHandler();
    this._removeScrollHandler && this._removeScrollHandler();
    this._removeMouseMoveHandler && this._removeMouseMoveHandler();
    this._removeScrollDebouncedHandler && this._removeScrollDebouncedHandler();
  }

  createPage = (pageIndex, topPosition = 0, pageViewHeight = 0) => {
    const PageContainer = this.props.PageContainer;
    return (
      <PageContainer
        key={pageIndex}
        pageIndex={pageIndex}
        fitPageTopPosition={topPosition}
        fitPageViewHeight={pageViewHeight}
      />
    );
  };

  getPagesView = () => {
    const {
      currPageIndex,
      viewPortWidth,
      showQuickTools,
      parentViewHeight,
      pagePositionFromTop,
      pageByPageWindowSize
    } = this.props;
    const start = currPageIndex - pageByPageWindowSize;
    const end = currPageIndex + pageByPageWindowSize;
    let topPosition = 0,
      pagesViewArray = [];
    for (let index = start; index <= end; index++) {
      if (isValidPageIndex(stores, index)) {
        const pageViewHeight = index === currPageIndex ? Math.max(parentViewHeight, getPageHeight(stores, index)) : 0;
        pagesViewArray.push(this.createPage(index, topPosition, pageViewHeight));
        topPosition += Math.max(parentViewHeight, getPageHeight(stores, index));
      }
    }
    const pagesViewStyle = {
      position: "sticky",
      top: pagePositionFromTop,
      height: Math.max(parentViewHeight, getPageHeight(stores, currPageIndex)),
      width: Math.max(
        getPageWidth(stores, currPageIndex) + (showQuickTools ? PAGES_VIEW_MARGIN_LEFT + PAGES_VIEW_MARGIN_RIGHT : 0),
        viewPortWidth
      )
    };
    return <div style={pagesViewStyle}>{pagesViewArray}</div>;
  };

  render() {
    const style = {
      height: this.props.parentViewHeight,
      overflow: "auto"
    };
    if (this.props.bgColor) {
      style.backgroundColor = this.props.bgColor;
    }

    let scrollbarDivStyle;
    if (this.props.isFitOneFullPageMode) {
      const extraPixels = getTotalPages(stores) === 1 ? 0 : 2;
      scrollbarDivStyle = {
        height:
          getPagesViewTotalHeight(stores) - Math.max(this.props.parentViewHeight, this.props.pageHeight) + extraPixels,
        width: "100%"
      };
    }
    const pagesView = this.getPagesView();

    const shouldShowCrossIcon = this.props.isFullScreenMode && (isTouchDevice() || this.state.showCrossIcon);

    return (
      <div
        id="pages-view"
        className={PageByPageViewCss.PageByPageView}
        style={style}
        ref={handle => {
          this.elementHandle = handle;
          this.props.setElementHandle(handle);
        }}
        onClick={this._handleClick}
        onWheel={this._handleWheelEvent}
        onMouseDown={this._handleMouseDown}
        aria-label={this.props.intl.formatMessage({ id: "accessibility.containerLabel" })}
        role="main"
        tabIndex={0}
      >
        {shouldShowCrossIcon ? (
          <div className={PageByPageViewCss.crossIcon} onClick={this.props.crossIconClickHandler}>
            <CustomIcon url={CrossIcon} />
          </div>
        ) : null}
        {this.props.shouldShowScrubber ? <ScrubberContainer /> : null}
        {pagesView}
        {this.props.isFitOneFullPageMode ? <div style={scrollbarDivStyle} /> : null}
      </div>
    );
  }
}

PageByPageView.defaultProps = {
  setElementHandle: () => {}
};

export { PageByPageView };
